import React, { useState, useCallback } from "react";
import { UserPlus, ImageIcon } from "lucide-react";
import { useJudgeProfiles } from "../../../hooks/useJudgeProfiles";
import { usePhotoUpload } from "../../../hooks/usePhotoUpload";
import { JudgeCard } from "../../../components/judging/JudgeCard";
import { CardHeader } from "../../../components/ui/CardHeader";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { ErrorAlert } from "../../../components/ui/ErrorAlert";

/**
 * Settings component for managing judge profiles
 */
export function JudgeProfilesSettings() {
  const {
    judges: serverJudges,
    loading,
    error,
    createJudge,
    updateJudge,
    deleteJudge
  } = useJudgeProfiles();
  
  const {
    isUploading,
    uploadProgress,
    error: uploadError,
    uploadPhoto
  } = usePhotoUpload();
  
  const [editingJudgeId, setEditingJudgeId] = useState(null);
  const [tempJudges, setTempJudges] = useState([]);

  // Combine server judges with temporary local judges
  const judges = [...serverJudges, ...tempJudges];

  const handleAddJudge = useCallback(() => {
    // Create a temp ID for newly created judge
    const newId = `temp_${Date.now()}`;
    const tempJudge = {
      id: newId,
      name: "",
      title: "",
      defaultNote: "",
      photo: "",
      photoUrl: null,
      isTemp: true
    };
    
    // Add to temporary judges array instead of saving to server
    setTempJudges(prev => [...prev, tempJudge]);
    setEditingJudgeId(newId);
  }, []);

  const handleSaveJudge = useCallback(async (id, updatedData) => {
    try {
      const isNew = id.toString().startsWith("temp_");
  
      if (isNew) {
        // Create new judge on server
        await createJudge(updatedData);
        // Remove from temporary judges array
        setTempJudges(prev => prev.filter(judge => judge.id !== id));
      } else {
        // Update existing judge on server
        await updateJudge(id, updatedData);
      }
  
      setEditingJudgeId(null);
      return true;
    } catch (error) {
      console.error("Error saving judge:", error);
      return false;
    }
  }, [createJudge, updateJudge]);

  const handleRemoveJudge = useCallback(async (judgeId) => {
    const isTemp = judgeId.toString().startsWith("temp_");
    
    if (isTemp) {
      // Remove from temporary judges array
      setTempJudges(prev => prev.filter(judge => judge.id !== judgeId));
      // Clear editing state if this was the judge being edited
      if (editingJudgeId === judgeId) {
        setEditingJudgeId(null);
      }
    } else {
      // Delete from server
      await deleteJudge(judgeId);
    }
  }, [deleteJudge, editingJudgeId]);

  const handleCancelEdit = useCallback((judgeId) => {
    const isTemp = judgeId.toString().startsWith("temp_");
    
    if (isTemp) {
      // Remove temporary judge if user cancels without saving
      setTempJudges(prev => prev.filter(judge => judge.id !== judgeId));
    }
    
    // Clear editing state
    setEditingJudgeId(null);
  }, []);

  const handlePhotoChange = useCallback(async (judgeId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    try {
      const result = await uploadPhoto({
        file,
        entityType: 'judges',
        entityId: judgeId,
        onSuccess: (fileUrl) => {
          const isTemp = judgeId.toString().startsWith("temp_");
          
          if (isTemp) {
            // Update temporary judge
            setTempJudges(prev => prev.map(judge => 
              judge.id === judgeId 
                ? { ...judge, photo: fileUrl, photoUrl: fileUrl }
                : judge
            ));
          } else {
            // Update server judge
            const judge = serverJudges.find(j => j.id === judgeId);
            if (judge) {
              updateJudge(judgeId, {
                ...judge,
                photoUrl: fileUrl // Use photoUrl field for backend consistency
              });
            }
          }
        }
      });
      
      // Return the photo URL for use in the save function
      return result;
    } catch (error) {
      // Error handling is done inside the hook
      throw error;
    }
  }, [serverJudges, updateJudge, uploadPhoto]);

  if (loading && judges.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="card bg-base-100 border border-base-300">
      <div className="card-body p-6">
        <div className="flex items-center justify-between mb-6">
          <CardHeader
            icon={<UserPlus />}
            title="Judge Profiles"
            description="Create and manage judge profiles for your competitions"
          />
          <button onClick={handleAddJudge} className="btn btn-primary">
            <UserPlus className="w-4 h-4 mr-2" />
            Add Judge
          </button>
        </div>

        <ErrorAlert 
          message={error || uploadError}
          className="mb-4"
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {judges.map((judge) => {
            const isEditing = judge.id === editingJudgeId;
            const isCurrentlyUploading = isUploading[judge.id] === true;
            
            return (
              <JudgeCard
                key={judge.id}
                judge={{...judge, photo: judge.photoUrl || judge.photo}}
                isEditing={isEditing}
                isUploading={isCurrentlyUploading}
                uploadProgress={uploadProgress[judge.id]}
                onEdit={setEditingJudgeId}
                onRemove={handleRemoveJudge}
                onSave={handleSaveJudge}
                onPhotoChange={handlePhotoChange}
                onCancel={handleCancelEdit}
              />
            );
          })}
          
          {judges.length === 0 && !loading && (
            <div className="col-span-full text-center py-8">
              <div className="max-w-sm mx-auto bg-gradient-to-b from-base-200 to-base-300 p-6 rounded-lg border border-base-300 shadow-md">
                <div className="w-20 h-20 rounded-full bg-base-100 flex items-center justify-center mx-auto mb-4">
                  <ImageIcon className="w-10 h-10 opacity-40" />
                </div>
                <h3 className="font-bold text-lg mb-2">No Judges Added</h3>
                <p className="text-base-content/60 mb-4">
                  Create judge profiles with photos and specialty information for your competitions.
                </p>
                <button
                  onClick={handleAddJudge}
                  className="btn btn-primary btn-sm"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Your First Judge
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 